import { useEffect, useRef, useState } from 'react';

const storageKey = 'heartland-display-v1';
type Preferences = { text: 'normal' | 'large' | 'extra'; contrast: 'normal' | 'light' | 'dark' };
const defaults: Preferences = { text: 'normal', contrast: 'normal' };
export function readPreferences(): Preferences {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
    return {
      text: ['normal', 'large', 'extra'].includes(saved?.text) ? saved.text : 'normal',
      contrast: ['normal', 'light', 'dark'].includes(saved?.contrast) ? saved.contrast : 'normal'
    };
  } catch { return defaults; }
}

export function DisplayOptions() {
  const [preferences, setPreferences] = useState<Preferences>(readPreferences);
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const update = () => setShowBackToTop(window.scrollY > 600);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  const backToTop = () => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reducedMotion ? 'instant' : 'smooth' });
    document.querySelector<HTMLAnchorElement>('.site-header .brand')?.focus({ preventScroll: true });
  };
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    document.documentElement.dataset.textSize = preferences.text;
    document.documentElement.dataset.contrast = preferences.contrast;
    try { localStorage.setItem(storageKey, JSON.stringify(preferences)); } catch { /* Private browsing may disable storage; controls still work. */ }
    return () => {
      delete document.documentElement.dataset.textSize;
      delete document.documentElement.dataset.contrast;
    };
  }, [preferences]);
  return <div className="display-toolbar">
    {showBackToTop && <button type="button" className="back-to-top" aria-label="Back to top" title="Back to top" onClick={backToTop}><span aria-hidden="true">↑</span></button>}
    <button type="button" ref={trigger} className="display-trigger" aria-haspopup="dialog" aria-controls="display-options" onClick={() => dialog.current?.showModal()}>Display options</button>
    <dialog id="display-options" className="display-dialog" ref={dialog} aria-labelledby="display-options-title" aria-describedby="display-options-help" onClose={() => trigger.current?.focus()}>
      <h2 id="display-options-title">Display options</h2>
      <p id="display-options-help">Choose what is easiest to read. Your choices stay on this device. Browser zoom works too.</p>
      <fieldset><legend>Text size</legend>
        {([['normal', 'Normal (100%)'], ['large', 'Large (125%)'], ['extra', 'Extra large (200%)']] as const).map(([value, label]) => <label key={value}><input type="radio" name="display-text" value={value} checked={preferences.text === value} onChange={() => setPreferences({ ...preferences, text: value })} />{label}</label>)}
      </fieldset>
      <fieldset><legend>Colors</legend>
        {([['normal', 'Original colors'], ['light', 'High contrast — light'], ['dark', 'High contrast — dark']] as const).map(([value, label]) => <label key={value}><input type="radio" name="display-contrast" value={value} checked={preferences.contrast === value} onChange={() => setPreferences({ ...preferences, contrast: value })} />{label}</label>)}
      </fieldset>
      <div className="display-dialog-actions"><button type="button" onClick={() => setPreferences(defaults)}>Reset to original</button><button type="button" onClick={() => dialog.current?.close()}>Done</button></div>
    </dialog>
  </div>;
}
